import {cancel, HybirdBridge, invoke, on} from "@hybird/core/bridge/hybirdBridge";
import {BridgeMethod} from "@hybird/core/bridge/core";
import {WebError} from "@hybird/core/error";
import {flutterError} from "@hybird/flutter/flutterError";
import {xda} from "@hybird/flutter/bridge";

export class FlutterUsbDevice extends BridgeMethod {

  /// Event passed to usbEventStream when a USB device is attached.
  static ACTION_USB_ATTACHED = "android.hardware.usb.action.USB_DEVICE_ATTACHED";

  /// Event passed to usbEventStream when a USB device is detached.
  static ACTION_USB_DETACHED = "android.hardware.usb.action.USB_DEVICE_DETACHED";

  constructor() {
    super();
  }

  /**
   *
   * @param address
   * @param baudRate
   * @param dataBits
   * @param flowControl
   * @param parity
   * @param stopBits
   * @return {Promise<boolean>}
   */
  connectUsbDevice({
                     address, baudRate = 115200, dataBits = 8, flowControl = 'none', parity = 'none', stopBits = 1
                   }) {
    return invoke("connectUsbDevice", {
      params: {
        address, baudRate, dataBits, flowControl, parity, stopBits
      }
    });
  }

  disconnectUsbDevice(address) {
    return invoke("disconnectUsbDevice", {
      params: {
        address
      }
    });
  }

  usbDevices({vid, pid} = {}) {
    return invoke("usbDevices").then(result => {
      if (result) {
        return result.filter(data => (vid == null || data.vid === vid) && (pid == null || data.pid === pid));
      }

      return result;
    });
  }

  onUsbDeviceValueChange(address, callback, notify = true) {
    return on("onUsbDeviceValueChange", callback, {
      isSend: true, params: {
        address, notify
      }
    });
  }

  async offUsbDeviceValueChange(callback) {
    return cancel("onUsbDeviceValueChange", {event: callback, isSend: true});
  }

  onUsbDeviceStateChange(callback) {
    return on("onUsbDeviceStateChange", callback, {isSend: true});
  }

  offUsbDeviceStateChange(callback) {
    return cancel("onUsbDeviceStateChange", {event: callback, isSend: true});
  }

  notifyUsbDeviceValueChange(address) {
    return invoke("notifyUsbDeviceValueChange", {
      params: {
        address
      }
    });
  }

  writeUsbDeviceValue(address, value) {
    return invoke("writeUsbDeviceValue", {
      params: {
        address, value
      }
    });
  }
}

export class FlutterConnectUsbDevice {

  _config = {
    name: "串口设备",
    baudRate: 115200,
    dataBits: 8,
    flowControl: 'none',
    parity: 'none',
    stopBits: 1,
    onUsbDeviceValueChange: undefined,
    onUsbDeviceStateChange: undefined,
    vid: undefined,
    pid: undefined
  }

  /**
   *
   * @param config{{
   *   name: "串口设备",
   *     baudRate: 115200,
   *     dataBits: 8,
   *     flowControl: 'none',
   *     parity: 'none',
   *     stopBits: 1,
   *     onUsbDeviceValueChange: function(event),
   *     onUsbDeviceStateChange: function(event),
   *     vid: Number,
   *     pid: Number
   * }}
   */
  async connectUsbDevice(config = {}) {
    const deviceConfig = Object.assign(this._config, config);

    if (this.connected) {
      return deviceConfig;
    }

    this.connected = false;
    const self = this;

    // {vid: 6790, pid: 29987}
    const device = await this.getUsbDevice(deviceConfig);
    deviceConfig.address = this._address = device.address;

    if (!this.connected) {
      this.connected = await xda.connectUsbDevice({
        baudRate: deviceConfig.baudRate,
        dataBits: deviceConfig.dataBits,
        flowControl: deviceConfig.flowControl,
        parity: deviceConfig.parity,
        stopBits: deviceConfig.stopBits,
        address: deviceConfig.address
      });

      if (this.connected) {
        this.onUsbDeviceValueChange?.cancel();
        this.onUsbDeviceStateChange?.cancel();
        await xda.notifyUsbDeviceValueChange(deviceConfig.address);
        this.onUsbDeviceValueChange = await xda.onUsbDeviceValueChange(deviceConfig.address, async event => {
          if (this._config.onUsbDeviceValueChange === deviceConfig.onUsbDeviceValueChange) {
            this._config.onUsbDeviceValueChange?.(event);
          } else {
            this._config.onUsbDeviceValueChange?.(event);
            deviceConfig.onUsbDeviceValueChange?.(event)
          }
        })

        this.onUsbDeviceStateChange = await xda.onUsbDeviceStateChange(async event => {
          console.log(event);

          //上下线切换
          if (event.onUsbDeviceStateChange === FlutterUsbDevice.ACTION_USB_ATTACHED) {
            if (this.connected) {
              await this.connectUsbDevice(deviceConfig);
            }
          } else {
            this._clearConfig();
          }

          deviceConfig.onUsbDeviceStateChange?.(event)
        })
      } else {
        throw WebError.code(flutterError.NOT_CONNECT_USB_DEVICE, {msg: `${config.name}连接失败`});
      }
    }

    return deviceConfig;
  }

  async getUsbDevice(config = this.usbDeviceConfig) {
    const devices = await flutterUsbDevice.usbDevices(config);
    const msg = `vid=${config.vid}, pid=${config.pid}`;

    if (devices.length > 1) {
      console.warn(`超过多个串口设备, ${msg}`);
    } else if (devices.length === 0) {
      console.warn(`超过多个串口设备, ${msg}`);
      throw WebError.code(flutterError.NOT_FIND_USB_DEVICE, {msg: `${config.name}不存在`});
    }

    return devices[devices.length - 1];
  }

  _clearConfig() {
    this.onUsbDeviceValueChange?.cancel();
    this.onUsbDeviceStateChange?.cancel();
    this._address = null;
    this.onUsbDeviceValueChange = null;
    this.onUsbDeviceStateChange = null;
    this.connected = false;
  }

  async disconnectUsbDevice() {
    if (this.connected) {
      await xda.disconnectUsbDevice(this._address);
      this._clearConfig();
    }
  }
}

export const flutterUsbDevice = new FlutterUsbDevice();